'use client';
import React, { useEffect } from "react";
import { RichText as RichTextConverter } from "@payloadcms/richtext-lexical/react";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "framer-motion";

export const renderRichTextAsHTML = (
  richText: any,
  isDescription?: boolean,
  textColor?: string,
  className?: string
) => {
  if (typeof richText === "string") {
    if (richText.includes("<b>") || richText.includes("<strong>")) {
      return <span dangerouslySetInnerHTML={{ __html: richText }} />;
    }
    return <span>{richText}</span>;
  }

  if (!richText?.root?.children) return "";
  const elements: React.JSX.Element[] = [];
  
  richText.root.children.forEach((paragraph: any, paragraphIndex: number) => {
    if (!paragraph.children) return;
    
    paragraph.children.forEach((textNode: any, index: number) => {
      const key = `p${paragraphIndex}-t${index}`;
      
      if (textNode.format === 1) {
        elements.push(
          <span className={`${textColor} text-xl-bold text-neutral-900 md:text-2xl-bold xl:text-3xl-bold`} key={key}>
            {textNode.text}
          </span>
        );
      } else {
        elements.push(
          <span
            className={`${
              !isDescription &&
              "text-xl-light text-neutral-900 md:text-2xl-light md:max-w-[90%] xl:text-3xl-light "
            } ${textColor && textColor} ${className && className}`}
            key={key}
          >
            {textNode.text}
          </span>
        );
      }
    });
    
    if (paragraphIndex < richText.root.children.length - 1) {
      elements.push(<span key={`br-${paragraphIndex}`} dangerouslySetInnerHTML={{ __html: "<br>" }} />);
    }
  });

  return <>{elements}</>;
};

const isSimpleText = (description: any): boolean => {
  if (typeof description === "string") return true;
  
  if (description?.root?.children) {
    const hasOnlyText = description.root.children.every((paragraph: any) => {
      return paragraph.children?.every((node: any) => 
        node.type === "text" && !node.format
      );
    });
    return hasOnlyText;
  }
  
  return false;
};

const extractSimpleText = (description: any): string => {
  if (typeof description === "string") return description;
  
  if (description?.root?.children) {
    return description.root.children
      .map((paragraph: any) => 
        paragraph.children?.map((node: any) => node.text).join("") || ""
      )
      .join("\n");
  }
  
  return "";
};

export default function Heading({
  title,
  description,
  titleEffectStyle,
  notitleEffect = false,
}: {
  title: any;
  description?: string | any;
  titleEffectStyle?: string;
  notitleEffect?: boolean;
}) {
  const gradientColor =
    titleEffectStyle === "secondary" ? "#E16F60" : "#F1F6F3";
  const textColor =
    titleEffectStyle === "secondary" ? "text-white" : "text-neutral-900";
  
  const controls = useAnimation();
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });
  
 useEffect(() => {
  if (isInView) {
    controls.start({ 
      backgroundSize: "100% 50%",
      backgroundPosition: "left top",
      transition: { duration: 1.5, ease: "easeInOut" } 
    });
  }
}, [isInView, controls]);
  const shouldUseSimpleText = description && isSimpleText(description);

  return (
    <div>
      <div className="text-xl-regular md:text-2xl-regular xl:text-3xl-regular">
        <motion.h2
          ref={ref}
          className={`text-xl-regular md:text-2xl-regular md:max-w-[90%] xl:text-3xl-regular ${textColor && textColor} bg-no-repeat bg-top`}
          style={{
            display: "inline",
            boxDecorationBreak: "clone",
            WebkitBoxDecorationBreak: "clone",
            backgroundImage: `${notitleEffect ? "none" : `linear-gradient(to bottom,${gradientColor},${gradientColor})`}`,
          }}
          initial={{ backgroundSize: "0% 50%", backgroundPosition: "right top" }}
          animate={controls}
        >
          {renderRichTextAsHTML(title, false, textColor)}
        </motion.h2>
        {description && (
          <div className="mt-16">
            {shouldUseSimpleText ? (
              <p className="text-m-regular text-neutral-900">
                {extractSimpleText(description)}
              </p>
            ) : (
              <RichTextConverter
                className="text-m-regular text-neutral-900"
                data={description as any}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
